import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { Tooltip } from "react-tooltip";
import { IoSettingsOutline } from "react-icons/io5";
import { GiFilmSpool } from "react-icons/gi";
import { IoIosColorFilter } from "react-icons/io";
import { BiColor } from "react-icons/bi";
import { FiStopCircle } from "react-icons/fi";
import { RiRecordCircleLine } from "react-icons/ri";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { RiShoppingBasketFill } from "react-icons/ri";
import { updatePrinterStatus } from "../../services/filamentService";

const colorStatus = {
  available: "#659c47",
  bussy: "#cd5752",
  maintenance: "#fbc24e",
};

const Wrapper = styled.div`
  width: 100%;
  padding: 1rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const ActivePrintersContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const Printer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: baseline;
`;
const PrinterImage = styled.img<{ $isActive?: boolean }>`
  width: ${(props) => (props.$isActive ? "220px" : "180px")};
  height: ${(props) => (props.$isActive ? "220px" : "180px")};
  transition: all 0.3s ease;
`;

const PrinterStatus = styled.div<{ color: string; $isActive?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  border: 1px solid white;
  position: relative;
  right: 40px;
  bottom: ${(props) => (props.$isActive ? "14px" : "4px")};
`;

const PrinterSubmenu = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  @media (min-width: 820px) {
    flex-direction: row;
  }
`;

const GeneralAction = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: end;
  gap: 1.5rem;
  background-color: white;
  border-radius: 8px;
  padding: 10px 20px 20px 20px;

  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  svg {
    font-size: 2.5rem;
  }

  @media (max-width: 820px) {
    gap: 1rem;
  }
`;

const PrinterAction = styled.div`
  display: flex;

  flex-direction: row;

  gap: 1.5rem;

  @media (max-width: 820px) {
    gap: 1rem;
  }

  svg {
    font-size: 2.5rem;
  }
`;
const ActionItem = styled.button<{ $active?: boolean }>`
  justify-content: center;
  align-items: center;
  display: flex;
  cursor: pointer;
  background-color: rgba(241, 239, 237, 0.81);
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 6px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  color: ${(props) => (props.$active ? "#3f5534" : "hsl(96, 2.40%, 41.00%)")};
  svg {
    font-size: 2.5rem;
    width: 2.5rem;
    height: 2.5rem;

    @media (max-width: 820px) {
      font-size: 2rem;
      width: 2rem;
      height: 2rem;
    }
  }
`;

const EmptyPrinters = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const NoData = styled.h3``;
const PrinterTitle = styled.h3`
  margin: 0;
  padding-bottom: 10px;
  text-transform: uppercase;
  font-family: Verdana, sans-serif;
  color: var(--secondary-font-color);
`;

interface Props {
  printers: any[];
  refetch: any;
}

const ActivePrinterComponent: React.FC<Props> = ({ printers, refetch }) => {
  const navigate = useNavigate();

  const [activePrinterIndex, setActivePrinterIndex] = useState(-1);

  const handleStatusChange = async (
    index,
    printerId: number,
    status: keyof typeof colorStatus
  ) => {
    try {
      await updatePrinterStatus(printerId, status);
      refetch();
    } catch (error) {
      alert(
        "A apărut o eroare la update-ul statusului. Te rugăm să încerci din nou mai târziu."
      );
    }
  };

  return (
    <Wrapper data-testid="printer-wrapper">
      {printers && printers.length > 0 ? (
        <Scrollbars style={{ width: "100%", height: 230 }}>
          <ActivePrintersContainer data-testid="active-printers">
            {printers.map((printer, index) => (
              <Printer
                key={printer.printerID}
                onClick={() => {
                  setActivePrinterIndex(index);
                  navigate(`printer/${printers[index].printerID}`);
                }}
              >
                <PrinterImage
                  $isActive={index === activePrinterIndex}
                  src={
                    printers[index].printerImage
                      ? `data:image/png;base64,${printers[index].printerImage}`
                      : "/images/noPrinterImage.png"
                  }
                  alt="printer-img"
                />
                <PrinterStatus
                  $isActive={index === activePrinterIndex}
                  color={colorStatus[printer.status]}
                />
              </Printer>
            ))}
          </ActivePrintersContainer>
        </Scrollbars>
      ) : (
        <EmptyPrinters>
          <PrinterImage
            style={{ height: "150px", width: "auto" }}
            src={"/images/noPrinters.png"}
            alt="printer-empty"
          />
          <NoData>Nu există nicio imprimantă înregistrată</NoData>
        </EmptyPrinters>
      )}

      <PrinterSubmenu>
        <GeneralAction style={{ display: "block" }}>
          {printers && printers.length > 0 && activePrinterIndex > -1 && (
            <PrinterTitle>{printers[activePrinterIndex]["name"]}</PrinterTitle>
          )}
          <PrinterAction>
            <ActionItem
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Staus nou: disponibilă"
              $active={activePrinterIndex > -1}
              onClick={() => {
                activePrinterIndex > -1 &&
                  handleStatusChange(
                    activePrinterIndex,
                    printers[activePrinterIndex].printerID,
                    "available"
                  );
              }}
            >
              <RiRecordCircleLine />
            </ActionItem>
            <ActionItem
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Staus nou: ocupată"
              $active={activePrinterIndex > -1}
              onClick={() => {
                activePrinterIndex > -1 &&
                  handleStatusChange(
                    activePrinterIndex,
                    printers[activePrinterIndex].printerID,
                    "bussy"
                  );
              }}
            >
              <FiStopCircle />
            </ActionItem>
            <ActionItem
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Staus nou: mentenanță"
              $active={activePrinterIndex > -1}
              onClick={() => {
                activePrinterIndex > -1 &&
                  handleStatusChange(
                    activePrinterIndex,
                    printers[activePrinterIndex].printerID,
                    "maintenance"
                  );
              }}
            >
              <HiOutlineExclamationCircle />
            </ActionItem>
            {/* this feature was crated in scop of disseration and was disable in  production */}              
            {/* <ActionItem
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Filamente compatibile"
              $active={activePrinterIndex > -1}
              onClick={() =>
                activePrinterIndex > -1 &&
                navigate(
                  `filament/${
                    activePrinterIndex > -1
                      ? printers[activePrinterIndex].printerID
                      : 0
                  }`
                )
              }
            >
              <BiColor />
            </ActionItem> */}
            <ActionItem
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Detalii tehnice"
              $active={activePrinterIndex > -1}
              onClick={() =>
                activePrinterIndex > -1 &&
                navigate(
                  `printer/${
                    activePrinterIndex > -1
                      ? printers[activePrinterIndex].printerID
                      : 0
                  }`
                )
              }
            >
              <IoSettingsOutline />
            </ActionItem>
          </PrinterAction>
        </GeneralAction>
        <GeneralAction>
          <ActionItem
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Furnizori"
            $active={true}
            onClick={() => navigate("suppliers")}
          >
            <RiShoppingBasketFill />
          </ActionItem>
          <ActionItem
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Toate filamentele"
            $active={true}
            onClick={() => navigate("filaments")}
          >
            <GiFilmSpool />
          </ActionItem>
          <ActionItem
            data-tooltip-id="my-tooltip"
            data-tooltip-content="Setări filament"
            $active={true}
            onClick={() => navigate("types")}
          >
            <IoIosColorFilter />
          </ActionItem>
        </GeneralAction>
      </PrinterSubmenu>
      <Tooltip id="my-tooltip" />
    </Wrapper>
  );
};

export default ActivePrinterComponent;
